export interface MobileDeviceInfo {
  deviceType?: string
  sysVersion?: string
  appVersion?: string
  deviceId?: string
  userCountry?: string
}

export interface SupportRequest {
  name: string
  email: string
  phone?: string
  issue: string
  deviceInfo?: MobileDeviceInfo
}

/**
 * Submits a support request to the "Get Code" Google Sheet via a Google Apps
 * Script Web App.
 *
 * Setup:
 * 1. Open the Google Sheet and add a tab named exactly "Get Code".
 * 2. In the sheet, go to Extensions > Apps Script.
 * 3. Paste the Apps Script code below and deploy it as a Web App
 *    (Execute as: Me, Who has access: Anyone).
 * 4. Copy the deployment URL and set it as VITE_GOOGLE_SCRIPT_URL in your .env.
 *
 * --- Apps Script code ---
 * // Replace YOUR_SPREADSHEET_ID with the ID from your Google Sheet URL:
 * // https://docs.google.com/spreadsheets/d/YOUR_SPREADSHEET_ID/edit
 * const SPREADSHEET_ID = 'YOUR_SPREADSHEET_ID';
 *
 * function doPost(e) {
 *   const sheet = SpreadsheetApp.openById(SPREADSHEET_ID).getSheetByName('Get Code');
 *   const data = JSON.parse(e.postData.contents);
 *   sheet.appendRow([
 *     data.timestamp || new Date(),
 *     data.name,
 *     data.email,
 *     data.phone || '',
 *     data.issue,
 *     data.deviceType || '',
 *     data.sysVersion || '',
 *     data.appVersion || '',
 *     data.deviceId || '',
 *     data.userCountry || '',
 *   ]);
 *   return ContentService
 *     .createTextOutput(JSON.stringify({ result: 'success' }))
 *     .setMimeType(ContentService.MimeType.JSON);
 * }
 * -----------------------
 *
 * NOTE: Data is sent as JSON with Content-Type: text/plain to avoid a CORS
 * preflight. Apps Script reads the body via JSON.parse(e.postData.contents).
 * The response is opaque (mode: 'no-cors'), so we assume success as long as
 * fetch itself did not throw.
 */
export async function submitSupportRequest(data: SupportRequest): Promise<void> {
  const scriptUrl = import.meta.env.VITE_GOOGLE_SCRIPT_URL

  if (!scriptUrl) {
    throw new Error('VITE_GOOGLE_SCRIPT_URL is not set in your environment variables.')
  }

  const payload = {
    source: 'getcode',
    name: data.name.trim(),
    email: data.email.trim(),
    phone: data.phone?.trim() || 'Not provided',
    issue: data.issue.trim(),
    timestamp: new Date().toISOString(),
    deviceType: data.deviceInfo?.deviceType ?? '',
    sysVersion: data.deviceInfo?.sysVersion ?? '',
    appVersion: data.deviceInfo?.appVersion ?? '',
    deviceId: data.deviceInfo?.deviceId ?? '',
    userCountry: data.deviceInfo?.userCountry ?? '',
  }

  await fetch(scriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify(payload),
  })
}
